import type { InputProps } from '@chakra-ui/react';
import { InputGroup, Center, Input, CloseButton } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from '@halo/ui-chat-react';

interface SearchInputProps extends Omit<InputProps, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  showCloseButton?: boolean;
  onClose?: () => void;
  inputProps?: InputProps;
  autoFocus?: boolean;
}

const SearchInput = ({
  value,
  onChange,
  showCloseButton = true,
  onClose,
  inputProps,
  autoFocus = true,
}: SearchInputProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const composingRef = useRef(false);
  const [innerValue, setInnerValue] = useState(value);

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  const handleCompositionStart = () => {
    composingRef.current = true;
  };

  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    composingRef.current = false;
    onChange(e.currentTarget.value);
  };

  const focus = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleClose = useCallback(() => {
    onChange('');
    onClose?.();
    focus();
  }, [onChange, onClose, focus]);

  useEffect(() => {
    if (autoFocus) {
      focus();
    }
  }, [focus, autoFocus]);

  return (
    <InputGroup
      startElement={
        <Center flexShrink="0" fontSize="md">
          <SearchIcon />
        </Center>
      }
      endElement={
        value && showCloseButton ? (
          <CloseButton size="xs" onClick={handleClose} me="-2" />
        ) : undefined
      }
    >
      <Input
        ref={inputRef}
        value={innerValue}
        placeholder={t('filter.searchPlaceholder')}
        borderRadius="lg"
        h="2.57em"
        onChange={(e) => {
          setInnerValue(e.target.value);
          if (!composingRef.current) {
            onChange(e.target.value);
          }
        }}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        {...inputProps}
      />
    </InputGroup>
  );
};

const MemoizedSearchInput = memo(SearchInput);

export { MemoizedSearchInput as SearchInput };
